import React from 'react';
import { useState, useEffect, useCallback, memo } from 'react';
import { Line } from 'react-chartjs-2';
import styles from './weekly-pnl-chart.module.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { format, subDays, parseISO } from 'date-fns';
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import api, { DAILY_PNL } from '../../../infrastructure/config/api';

// Register ChartJS components once
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Custom tooltip formatter with null checks
const tooltipFormatter = (context) => {
  if (!context || !context.length || context[0] === undefined) {
    return ''; // Return empty string if context is invalid
  }

  const dataPoint = context[0];
  const label = dataPoint.label || '';
  const value = dataPoint.parsed !== undefined ? dataPoint.parsed : (dataPoint.raw || 0);
  const isProfit = value >= 0;

  return `${label}: ${isProfit ? '+' : ''}${Number(value).toFixed(2)} USDT`;
};

const StatBox = memo(({ title, value, secondaryValue, isPositive }) => {

  return (
    <Box textAlign="center" p={1}>
      <Typography variant="caption" color="textSecondary" display="block">
        {title}
      </Typography>
      <Typography
        variant="h6"
        color={isPositive ? 'success.main' : 'error.main'}
        fontWeight="bold"
      >
        {value}
      </Typography>
      {secondaryValue && (
        <Typography variant="caption" color="textSecondary">
          {secondaryValue}
        </Typography>
      )}
    </Box>
  );
});
StatBox.displayName = 'StatBox';

const WeeklyPnlChart = ({ lastTradesUpdate }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const [timeRange, setTimeRange] = useState('week'); // 'week', 'month', '3months'

  // Debug: Log khi prop lastTradesUpdate thay đổi
  useEffect(() => {
    console.log('📡 [WeeklyPnlChart] lastTradesUpdate prop changed:', lastTradesUpdate);
  }, [lastTradesUpdate]);

  const [chartOptions] = useState({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: tooltipFormatter
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value}`
        }
      }
    }
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({
    totalPnl: 0,
    profitableDays: 0,
    bestDay: { date: null, pnl: -Infinity },
    worstDay: { date: null, pnl: Infinity }
  });

  const fetchPnlData = useCallback(async () => {
    try {
      console.log('📊 [WeeklyPnlChart] fetchPnlData called, timeRange:', timeRange);
      setLoading(true);
      setError(null);

      // Debug: Log token and auth header
      const token = localStorage.getItem('token');
      console.log('  - Token exists:', !!token);
      console.log('  - Auth header set:', !!api.defaults.headers.common['Authorization']);

      let startDate, endDate = new Date();

      switch (timeRange) {
        case 'month':
          startDate = subDays(endDate, 29); // Last 30 days
          break;
        case '3months':
          startDate = subDays(endDate, 89); // Last 90 days
          break;
        case 'week':
        default:
          startDate = subDays(endDate, 6); // Last 7 days
      }

      const response = await api.get(DAILY_PNL.SUMMARY(
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd')
      ));
      const responseData = response.data;

      console.log('✅ [WeeklyPnlChart] P&L API Response received');
      console.log('  - Success:', responseData?.success);
      console.log('  - Has data:', !!(responseData?.data?.data?.length));

      if (responseData && responseData.success && responseData.data && responseData.data.data && responseData.data.data.length > 0) {
        const { dates, data: pnlData, total } = responseData.data;

        // Format dates for display
        const formattedDates = dates.map(date =>
          format(new Date(date), 'MMM dd')
        );

        // Extract PnL values from the data
        const pnlValues = pnlData.map(item => parseFloat(item.total));
        const totalPnl = total;

        // Calculate profitable days, best day, and worst day
        let profitableDays = 0;
        let bestDay = { date: null, pnl: -Infinity };
        let worstDay = { date: null, pnl: Infinity };

        pnlData.forEach((item, index) => {
          const pnl = parseFloat(item.total);

          if (pnl > 0) profitableDays++;

          if (pnl > bestDay.pnl) {
            bestDay = { date: dates[index], pnl };
          }

          if (pnl < worstDay.pnl) {
            worstDay = { date: dates[index], pnl };
          }
        });

        setStats({
          totalPnl,
          profitableDays,
          totalDays: dates.length,
          bestDay,
          worstDay
        });

        // Prepare chart data
        const chartData = {
          labels: formattedDates,
          datasets: [
            {
              label: 'Daily PnL (USDT)',
              data: pnlValues,
              borderColor: 'rgba(75, 192, 192, 0.8)',
              backgroundColor: 'rgba(75, 192, 192, 0.1)',
              tension: 0.4,
              fill: {
                target: 'origin',
                above: 'rgba(75, 192, 192, 0.1)',
                below: 'rgba(255, 99, 132, 0.1)'
              },
              pointBackgroundColor: pnlValues.map(pnl =>
                pnl >= 0 ? '#4caf50' : '#f44336'
              ),
              pointBorderColor: pnlValues.map(pnl =>
                pnl >= 0 ? '#4caf50' : '#f44336'
              ),
              pointHoverRadius: 6,
              pointHoverBorderWidth: 2,
              borderWidth: 2,
            }
          ]
        };

        setChartData(chartData);
        console.log('💹 [WeeklyPnlChart] Chart data updated successfully');
        console.log('  - Total PnL:', totalPnl);
        console.log('  - Days:', dates.length);

      } else {
        console.log('⚠️ [WeeklyPnlChart] No data received, clearing chart');
        setChartData(null);
      }
    } catch (err) {
      console.error('❌ [WeeklyPnlChart] Error fetching PnL data:', err);
      setError('Failed to load PnL data. Please try again.');
    } finally {
      setLoading(false);
      console.log('🏁 [WeeklyPnlChart] fetchPnlData completed');
    }
  }, [timeRange]);

  const handleSync = useCallback(async () => {
    try {
      setSyncing(true);
      const response = await api.post('/api/daily-pnl/sync');

      // The response data contains an array of synced records
      const syncedRecords = response.data.data || [];
      const syncedSymbols = syncedRecords.length;

      // Calculate total PnL from synced records if needed
      const totalPnl = syncedRecords.reduce((sum, record) => sum + (record.pnl || 0), 0);

      setSnackbar({
        open: true,
        message: `Synced ${syncedSymbols} symbols${totalPnl !== undefined ? `. Total PnL: ${totalPnl.toFixed(2)}` : ''}`,
        severity: 'success'
      });

      // Refresh the chart data after successful sync
      fetchPnlData();
    } catch (err) {
      console.error('Error syncing PnL:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers
      });

      if (err.response?.status === 401) {
        // Handle unauthorized - token might be expired
        console.log('Authentication failed, redirecting to login');
        window.location.href = '/login';
        return;
      }

      setError('Không thể tải dữ liệu PnL. Vui lòng thử lại sau.');
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Lỗi khi tải dữ liệu PnL',
        severity: 'error'
      });
    } finally {
      setSyncing(false);
    }
  }, [fetchPnlData]);

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Clear state khi component mount để tránh hiển thị data cũ
  useEffect(() => {
    console.log('🧹 WeeklyPnlChart mounted, clearing old data...');
    setChartData(null);
    setLoading(true);
    setError(null);
    setStats({
      totalPnl: 0,
      profitableDays: 0,
      bestDay: { date: null, pnl: -Infinity },
      worstDay: { date: null, pnl: Infinity }
    });
  }, []); // Chỉ chạy khi mount

  // Fetch data on component mount and when timeRange changes
  useEffect(() => {
    console.log('WeeklyPnlChart mounted or timeRange changed');
    fetchPnlData().catch((err) => {
      console.error('Unhandled error in fetchPnlData:', err);
    });
  }, [fetchPnlData, timeRange]);

  // Auto-refresh when running trades update (triggered by Dashboard polling)
  useEffect(() => {
    // Skip nếu lastTradesUpdate chưa được set (lần mount đầu tiên)
    if (!lastTradesUpdate) {
      console.log('⏭️ [WeeklyPnlChart] Skipping auto-refresh, lastTradesUpdate is null (initial mount)');
      return;
    }

    console.log('⚡ [WeeklyPnlChart] Running trades updated, auto-refreshing P&L chart...');
    console.log('  - lastTradesUpdate timestamp:', new Date(lastTradesUpdate).toLocaleTimeString());

    fetchPnlData().catch((err) => {
      console.error('❌ [WeeklyPnlChart] Error auto-refreshing P&L data:', err);
    });
  }, [lastTradesUpdate, fetchPnlData]);
  const renderTimeRangeButtons = useCallback(() => (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      sx={{
        flexWrap: { xs: 'wrap', sm: 'nowrap' }, // Allow wrapping on extra-small screens
        justifyContent: { xs: 'center', sm: 'flex-start' }, // Center buttons on extra-small screens
      }}
    >
      <ButtonGroup size="small" variant="outlined" sx={{ mb: { xs: 1, sm: 0 } }}> {/* Adjust margin-bottom */}
        <Button variant={timeRange === 'week' ? 'contained' : 'outlined'} onClick={() => setTimeRange('week')}>
          1W
        </Button>
        <Button variant={timeRange === 'month' ? 'contained' : 'outlined'} onClick={() => setTimeRange('month')}>
          1M
        </Button>
        <Button variant={timeRange === '3months' ? 'contained' : 'outlined'} onClick={() => setTimeRange('3months')}>
          3M
        </Button>
      </ButtonGroup>
      <Tooltip title="Sync PnL data" arrow>
        <Box sx={{ mb: { xs: 1, sm: 0 } }}> {/* Add margin-bottom on extra-small screens */}
          <IconButton
            onClick={handleSync}
            disabled={syncing}
            color="primary"
            size="small"
            sx={{ mr: 1, opacity: syncing ? 0.7 : 1 }}
          >
            <SyncIcon className={syncing ? styles.spin : ''} />
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  ), [fetchPnlData, handleSync, loading, syncing, timeRange]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
          textAlign="center"
          p={3}
        >
          <Typography variant="body1" color="error" gutterBottom>
            Error loading P&L data
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {error.message || 'Please try again later'}
          </Typography>
        </Box>
      );
    }

    if (!chartData) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
          textAlign="center"
          p={3}
        >
          <Typography variant="body1" color="textSecondary" gutterBottom>
            No P&L data available for the selected period
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Your realized P&L will appear here after you make some trades.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Box mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <StatBox
                title="Total P&L"
                value={`${stats.totalPnl >= 0 ? '+' : ''}${stats.totalPnl.toFixed(2)} USDT`}
                isPositive={stats.totalPnl >= 0}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatBox
                title="Profitable Days"
                value={`${stats.profitableDays} of ${stats.totalDays}`}
                secondaryValue={`${Math.round((stats.profitableDays / stats.totalDays) * 100)}% win rate`}
                isPositive={stats.profitableDays / stats.totalDays >= 0.5}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatBox
                title="Best Day"
                value={stats.bestDay.date ? format(parseISO(stats.bestDay.date), 'MMM d') : 'N/A'}
                secondaryValue={stats.bestDay.pnl !== -Infinity ? `${stats.bestDay.pnl.toFixed(2)} USDT` : 'N/A'}
                isPositive={stats.bestDay.pnl >= 0}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatBox
                title="Worst Day"
                value={stats.worstDay.date ? format(parseISO(stats.worstDay.date), 'MMM d') : 'N/A'}
                secondaryValue={stats.worstDay.pnl !== Infinity ? `${stats.worstDay.pnl.toFixed(2)} USDT` : 'N/A'}
                isPositive={stats.worstDay.pnl >= 0}
              />
            </Grid>
          </Grid>
        </Box>
        <Box height={300} position="relative">
          <Line data={chartData} options={chartOptions} />
        </Box>
      </>
    );
  };

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent={{ xs: 'center', sm: 'space-between' }}
          alignItems="center"
          flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
          mb={2}
        >
          <Typography variant="h6" component="div" sx={{ mb: { xs: 1, sm: 0 } }}>
            P&L Summary
            <Tooltip title="Shows your realized profit and loss over time">
              <InfoIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
            </Tooltip>
            <Tooltip title="Refresh data">
              <IconButton
                size="small"
                onClick={() => fetchPnlData(timeRange)}
                disabled={loading}
                sx={{ opacity: loading ? 0.7 : 1, ml: 1 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          {renderTimeRangeButtons()}
        </Box>
        {renderContent()}
      </CardContent>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Card>
  );
};

export default WeeklyPnlChart;
